import { Kit, KitType } from '@repo/types';
import { parseDate, sortKitsByResultsViewableOrLatest } from '@repo/utils';
import {
  ButtonColors,
  ButtonSizes,
  Card,
  LinkButton,
  Typography,
} from 'components';
import { Dropdown } from 'components/Elements';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import useKits from 'services/kits/useKits';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { ConsultPricing } from '../ConsultPricing/ConsultPricing';
import KitSelectOption from './KitSelectOption';

const texts: Record<string, { title: string; description: string }> = {
  simplified: {
    title: 'Book another 1-on-1 consult now',
    description:
      'Any extra doubts? No worry! we always can help to recover your gut.',
  },
  normal: {
    title: 'Schedule a results review',
    description:
      'Work through your results with our qualified microbiome specialists. If you are being asked to pay for a coaching session but believe you should have credits, please reach out to hello@tinyhealth.com',
  },
};

type Props = {
  variant?: 'normal' | 'simplified';
};

const ConsultBookingCard = ({ variant = 'normal' }: Props) => {
  const { data: kits } = useKits();
  const sortedKits = sortKitsByResultsViewableOrLatest(kits || []);
  const { trackEvent } = useAnalytics();

  const [selectedKit, setSelectedKit] = useState<Kit | undefined>(undefined);

  // Initialize with the kit when kits are loaded
  useEffect(() => {
    if (sortedKits.length === 1 && !selectedKit) {
      // If only one kit, default select it
      setSelectedKit(sortedKits[0]);
    } else if (sortedKits.length > 1 && !selectedKit) {
      // If multiple kits, don't auto-select (user needs to choose)
      setSelectedKit(undefined);
    }
  }, [sortedKits, selectedKit]);

  const consultLink = selectedKit
    ? `/results/${selectedKit.id}/consults/select-method`
    : '';

  const currentKitTypeIsStool = selectedKit?.sampling_type === KitType.STOOL;

  const kitSelectOptions = sortedKits.map((kit: Kit) => ({
    value: kit.id,
  }));

  const onChange = (value: string) => {
    const kit = sortedKits.find(k => k.id === value);
    setSelectedKit(kit);
  };

  // Check if surveys are incomplete for the selected kit
  const surveysIncomplete = selectedKit && !selectedKit.date_survey_completed;

  const scheduleDisabled = !selectedKit || surveysIncomplete;

  return (
    <Card>
      <div className='flex flex-column gap-3'>
        <Typography variant='heading-m'>{texts[variant].title}</Typography>
        <Dropdown
          options={kitSelectOptions}
          value={selectedKit?.id || ''}
          onChange={onChange}
          CustomOption={({ innerProps, ...props }) => (
            <KitSelectOption {...innerProps} {...props} />
          )}
          controlIconString={
            selectedKit ? (currentKitTypeIsStool ? 'swab' : 'lotus') : undefined
          }
          controlLabel={
            selectedKit?.id ||
            (sortedKits.length > 1 ? 'Select kit' : undefined)
          }
          controlSubLabel={
            selectedKit
              ? format(parseDate(selectedKit?.sample_date ?? ''), 'MM/dd/yy')
              : undefined
          }
          hideControlSubLabelMobile
          placement={'left'}
          absolute
        />
        <Typography variant='body-s'>{texts[variant].description}</Typography>
        {surveysIncomplete && (
          <Typography variant='label' color='red'>
            Complete surveys on Home before booking
          </Typography>
        )}
        <LinkButton
          label='Schedule a review'
          href={consultLink}
          disabled={scheduleDisabled}
          width={'full'}
          size={ButtonSizes.M}
          color={ButtonColors.PURPLE}
          onClick={() =>
            trackEvent('Schedule Call Clicked', { source: 'consult_booking' })
          }
        />
        <ConsultPricing />
      </div>
    </Card>
  );
};

export default ConsultBookingCard;
